const STUDENT_TLD_SUFFIXES = [".edu", ".ac.uk"];

/**
 * @param {string | null | undefined} email
 * @returns {string | null} The matched domain, or null if not a student email.
 */
export function detectStudentDomain(email) {
  if (!email) return null;
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return null;

  for (const suffix of STUDENT_TLD_SUFFIXES) {
    if (domain.endsWith(suffix)) return domain;
  }
  return null;
}

/** @param {string | null | undefined} email */
export function isStudentEmail(email) {
  return detectStudentDomain(email) !== null;
}

/**
 * Server-side eligibility gate for the student discount. Takes the full
 * authenticated user object (from Supabase auth) and returns whether the
 * caller qualifies for the student discount, plus the matched domain.
 *
 * Eligibility requires:
 *   - an authenticated user with a non-empty email
 *   - email_confirmed_at set (defence in depth — Supabase normally won't
 *     hand out a user with an unconfirmed email, but we check anyway)
 *   - the email domain matches the academic-domain list above
 *
 * This function intentionally does NOT read any DB flag. The source of
 * truth is the verified email on the auth user — any cached state in
 * user_credits is for UI convenience only and must not be trusted for
 * discount gating.
 *
 * @param {{ email?: string | null; email_confirmed_at?: string | null } | null | undefined} user
 * @returns {{ eligible: boolean; domain: string | null; reason?: string }}
 */
export function evaluateStudentEligibility(user) {
  if (!user) return { eligible: false, domain: null, reason: "unauthenticated" };
  if (!user.email) return { eligible: false, domain: null, reason: "no_email" };
  if (!user.email_confirmed_at) {
    return { eligible: false, domain: null, reason: "email_unconfirmed" };
  }
  const domain = detectStudentDomain(user.email);
  if (!domain) {
    return { eligible: false, domain: null, reason: "non_academic_domain" };
  }
  return { eligible: true, domain };
}
